import { useOnBehalfOfAccountId } from '@openx/components-deal/lib/DealForm/hooks/useOnBehalfOfAccountId.ts';
import type { DealProviderConfig } from '@openx/components-deal/lib/utils/context/DealContext.tsx';
import { useOrganizationsByIdsFetch } from '../../modules/deals/hooks/useOrganizationsByIdsFetch.ts';
import { useGetContextualPricing } from '../targeting/utils/useGetContextualPricing.ts';
import { useAmazonSyncStatusOptionsFetch } from './utils/amazonSync/useAmazonSyncStatusOptionsFetch.ts';
import { useDealInsightsFilterOptions } from './utils/dealInsights/useDealInsightsFilterOptions.ts';
import { useDealInsightsJobApi } from './utils/dealInsights/useDealInsightsJobApi.ts';
import { useFetchDealInsights } from './utils/dealInsights/useFetchDealInsights.ts';
import { useGetDealInsightsJobId } from './utils/dealInsights/useGetDealInsightsJobId.ts';
import { useDealOperations } from './utils/dealOperations/useDealOperations.ts';
import { useBuyersFetch } from './utils/participants/useBuyersFetch.ts';
import { useDemandPartnersFetch } from './utils/participants/useDemandPartnersFetch.ts';
import { useParticipantHooks } from './utils/participants/useParticipantHooks.ts';
import { usePdpSyncDealStatusOptionsFetch } from './utils/pdpSync/usePdpSyncDealStatusOptionsFetch.ts';
import { usePdpSyncNegotiationStatusOptionsFetch } from './utils/pdpSync/usePdpSyncNegotiationStatusOptionsFetch.ts';
import { usePdpSyncStatusOptionsFetch } from './utils/pdpSync/usePdpSyncStatusOptionsFetch.ts';
import { useDirectDealsPartnersFetch } from './utils/thirdPartyFees/useDirectDealsPartnersFetch.ts';
import { useThirdPartyPartnersFetch } from './utils/thirdPartyFees/useThirdPartyPartnersFetch.ts';
import {
  useDealTroubleshootingAgentMessageMutationState,
  useDealTroubleshootingAgentSendMessage,
  useDealTroubleshootingAgentSessionHistory,
  useDealTroubleshootingAgentSessionList,
} from './utils/troubleshootingAgent/useDealTroubleshootingAgent.ts';
import { useDealTroubleshootingJobFetch } from './utils/troubleshootingFunnel/useDealTroubleshootingJobFetch.ts';
import { useAccountOptionsFetch } from './utils/useAccountOptionsFetch.ts';
import { useAgenciesOptionsFetch } from './utils/useAgenciesOptionsFetch.ts';
import { useBrandsOptionsFetch } from './utils/useBrandsOptionsFetch.ts';
import { useCanEditSegmentPartnerPlatformShare } from './utils/useCanEditSegmentPartnerPlatformShare.ts';
import { useCurrencyFetch } from './utils/useCurrencyFetch.ts';
import { useDealDiscounts } from './utils/useDealDiscounts.ts';
import { useDealFetch } from './utils/useDealFetch.ts';
import { useDealForecast } from './utils/useDealForecast.ts';
import { useDealForecastActions } from './utils/useDealForecastActions.ts';
import { useDealFormActions } from './utils/useDealFormActions.ts';
import { useDealFormSelector } from './utils/useDealFormSelector.ts';
import { useDealFormSubmit } from './utils/useDealFormSubmit.ts';
import { useDealGroupFetch } from './utils/useDealGroupFetch.ts';
import { useDealHealthScoreRootCauseDetails } from './utils/useDealHealthScoreRootCauseDetails.ts';
import { useDealRboInfoFetch } from './utils/useDealRboInfoFetch.ts';
import { useDealSourceFetch } from './utils/useDealSourceFetch.ts';
import { useDealsFetch } from './utils/useDealsFetch.ts';
import { useDealsLandingListFetch } from './utils/useDealsLandingListFetch.ts';
import { useDealsListFetch } from './utils/useDealsListFetch.ts';
import { useDealsRowActions } from './utils/useDealsRowActions.tsx';
import { useDealsTableFetch } from './utils/useDealsTableFetch.tsx';
import { useDealTroubleshootingFeedback } from './utils/useDealTroubleshootingFeedback.ts';
import { useExpectedAdCategoryFetch } from './utils/useExpectedAdCategoryFetch.ts';
import { useFetchDealForecast } from './utils/useFetchDealForecast.ts';
import { useFetchDealIdGroupInfo } from './utils/useFetchDealIdGroupInfo.ts';
import { useGetForecastJobId } from './utils/useGetForecastJobId.ts';
import { useInitialValues, useInitialValuesLazy } from './utils/useInitialValues.ts';
import { useMonetizingFetch } from './utils/useMonetizingFetch.ts';
import { useMultipleDealsFetch } from './utils/useMultipleDealsFetch.ts';
import { usePackageFetch, usePackageLazyFetch } from './utils/usePackageFetch.ts';
import { useParticipantFunctions } from './utils/useParticipantFunctions.ts';
import { useRefreshAuthToken } from './utils/useRefreshAuthToken.ts';
import { useRelatedAccountsFetch } from './utils/useRelatedAccountsFetch.ts';
import { useSetMultiproviderEnabledDataPartner } from './utils/useSetMultiproviderEnabledDataPartner.ts';
import { useTroubleshootingFetch } from './utils/useTroubleshootingFetch.ts';
import { useValidationSchema } from './utils/useValidationSchema.ts';

const todoConfig = {
  useContentAttributesFetch: () => {
    throw new Error('useContentAttributesFetch not implemented.');
  },
  useCreativeTypesFetch: () => {
    throw new Error('useCreativeTypesFetch not implemented.');
  },
  useLanguagesFetch: () => {
    throw new Error('useLanguagesFetch not implemented.');
  },
  useMarketBrandGroupsFetch: () => {
    throw new Error('Function not implemented.');
  },
  useOnBehalfOfFetch: () => {
    throw new Error('useOnBehalfOfFetch not implemented.');
  },
  useOpenAuctionsAccessFetch: () => {
    throw new Error('useOpenAuctionsAccessFetch not implemented.');
  },
  usePostAuctionDiscountsFetch: () => {
    throw new Error('usePostAuctionDiscountsFetch not implemented.');
  },
  useFeePublisherPmpFetch: () => {
    throw new Error('useFeePublisherPmpFetch not implemented.');
  },
  useRebatesFetch: () => {
    throw new Error('useRebatesFetch not implemented.');
  },
  useDealRevenueBreakdownFetch: () => {
    throw new Error('useDealRevenueBreakdownFetch not implemented.');
  },
  useGlobalFeePublisherPmpFetch: () => {
    throw new Error('useGlobalFeePublisherPmpFetch not implemented.');
  },
  useTpfBoostedBidPriceFetch: () => {
    throw new Error('useTpfBoostedBidPriceFetch not implemented.');
  },
  useBuyerOrganizationsFetch: () => {
    throw new Error('useBuyerOrganizationsFetch not implemented.');
  },
} satisfies Partial<DealProviderConfig>;

export const dealConfig: DealProviderConfig = {
  ...todoConfig,
  isOa: true,
  useAccountOptionsFetch,
  useAgenciesOptionsFetch,
  useBrandsOptionsFetch,
  useBuyersFetch,
  useCurrenciesFetch: useCurrencyFetch,
  useCurrenciesFormFetch: () => ({
    currencies: useCurrencyFetch(),
  }),
  useDealFetch,
  useDealForecast,
  useDealForecastActions,
  useDealFormActions,
  useDealFormSelector,
  useDealGroupFetch,
  useDealHealthScoreRootCauseDetails,
  useDealOperations,
  useDealRboInfoFetch,
  useDealSourceFetch,
  useDealsFetch,
  useDealsLandingListFetch,
  useDealsListFetch,
  useDealsRowActions,
  useDealsTableFetch,
  useDemandPartnersFetch,
  useExpectedAdCategoryFetch,
  useFetchDealForecast,
  useFetchDealIdGroupInfo,
  useDealTroubleshootingJobFetch,
  useDealTroubleshootingFeedback,
  useDealTroubleshootingAgentSessionList,
  useDealTroubleshootingAgentSessionHistory,
  useDealTroubleshootingAgentSendMessage,
  useDealTroubleshootingAgentMessageMutationState,
  useRefreshAuthToken,
  useGetForecastJobId,
  useInitialValues,
  useInitialValuesLazy,
  useMonetizingFetch,
  useMultipleDealsFetch,
  usePackageFetch,
  usePackageLazyFetch,
  useParticipantFunctions,
  useParticipantHooks,
  useRelatedAccountsFetch,
  useThirdPartyPartnersFetch,
  useDirectDealsPartnersFetch,
  useTroubleshootingFetch,
  useValidationSchema,
  useDealInsightsFilterOptions,
  useDealInsightsJobApi,
  useGetDealInsightsJobId,
  useFetchDealInsights,
  useDealDiscounts,
  useCanEditSegmentPartnerPlatformShare,
  useSetMultiproviderEnabledDataPartner,
  useGetContextualPricing,
  useDealFormSubmit,
  useOrganizationsByIdsFetch,
  useOnBehalfOfAccountId,
  usePdpSyncDealStatusOptionsFetch,
  usePdpSyncNegotiationStatusOptionsFetch,
  usePdpSyncStatusOptionsFetch,
  useAmazonSyncStatusOptionsFetch,
};
